import {
	json,
	type LoaderFunctionArgs,
	type MetaFunction,
} from '@remix-run/node'
import { Link, useLoaderData, useSearchParams } from '@remix-run/react'
import { useState } from 'react'
import slugify from 'slugify'
import { Button } from '#app/components/ui/button.js'
import { Dialog, DialogContent } from '#app/components/ui/dialog'
import { Icon } from '#app/components/ui/icon.js'
import Works from '#app/components/works.js'
import { getUserId } from '#app/utils/auth.server.js'
import { prisma } from '#app/utils/db.server.js'

// const randomPick = (values: string[]) => {
// 	const index = Math.floor(Math.random() * values.length)
// 	return values[index]
// }

export const meta: MetaFunction<typeof loader> = ({ data }) => {
	return [
		{
			title: data
				? "Kogia's Oceanic Library"
				: "Kogia's Oceanic Library | Library (Error)",
		},
		{
			name: 'description',
			content: `A free library of high-quality photos and videos of the marine world, built for conservation.`,
		},
		{
			property: 'og:image',
			content: 'https://library.kogia.org/img/og-image.jpg',
		},
		{
			property: 'og:image:width',
			content: '600',
		},
		{
			property: 'og:image:height',
			content: '600',
		},
	]
}

export async function loader({ request }: LoaderFunctionArgs) {
	const user = await getUserId(request)
	const searchParams = new URL(request.url).searchParams
	const categories = [
		'ecosystems',
		'wildlife',
		'categories',
		'perspectives',
	].flatMap(cat => searchParams.getAll(cat))
	const searchTerm = searchParams.get('search')
	const typeOfWorks = searchParams.get('works')
	const artists = searchParams.getAll('contributors')
	const tags =
		searchTerm && searchTerm.trim() !== ''
			? searchTerm.split(/\s+/).map(tag => slugify(tag.toLocaleLowerCase()))
			: undefined
	const isSearch = Boolean(searchTerm && searchTerm.trim() !== '')
	const [works, meta] = await prisma.work
		.paginate({
			where: {
				...(isSearch && {
					OR: [
						{
							AND:
								tags &&
								tags.map(t => ({
									tags: {
										some: {
											slug: {
												in: [t],
											},
										},
									},
								})),
						},
						{
							name: { contains: searchTerm as string },
						},
						{
							description: { contains: searchTerm as string },
						},
					],
				}),
				...(categories &&
					categories.length > 0 && {
						categories: {
							some: {
								slug: {
									in: categories,
								},
							},
						},
					}),
				...(typeOfWorks === 'photos' && {
					photoId: {
						not: null,
					},
				}),
				...(typeOfWorks === 'videos' && {
					videoId: {
						not: null,
					},
				}),
				...(artists.length && {
					user: {
						username: {
							in: artists,
						},
					},
				}),
			},
			select: {
				id: true,
				name: true,
				originalAssetUrl: true,
				...(user && {
					collections: {
						where: {
							isQuickCollection: true,
							userId: user,
						},
						take: 1,
						select: { id: true },
					},
				}),
				photo: {
					select: {
						width: true,
						height: true,
					},
				},
				video: {
					select: {
						playbackId: true,
						maxWidth: true,
						maxHeight: true,
					},
				},
				user: {
					select: {
						name: true,
						username: true,
					},
				},
			},
			orderBy: {
				updatedAt: 'desc',
			},
		})
		.withPages({
			page: searchParams.get('page') ? Number(searchParams.get('page')) : 1,
			limit: 48,
			includePageCount: true,
		})
	return json({
		status: 'idle',
		works: works,
		meta,
		user,
	} as const)
}

export type LibraryLoaderType = typeof loader

function Pagination({
	current,
	total,
	next,
	previous,
	searchParams,
}: {
	current: number
	total: number
	next: number | null
	previous: number | null
	searchParams: URLSearchParams
}) {
	const stringSearchParams = searchParams.toString()
	return (
		<div className="flex max-w-full flex-wrap items-center justify-center gap-2 pb-32">
			{current === 1 ? (
				<Button
					tabIndex={-1}
					variant={'secondary'}
					className="cursor-not-allowed"
					size="sm"
				>
					<Icon name="chevron-left" className="cursor-not-allowed text-primary">
						<span className="sr-only">Previous Page</span>
					</Icon>
				</Button>
			) : (
				<Button size="sm" asChild variant={'outline'}>
					<Link
						to={`/?${stringSearchParams.replace(/page=\d+/, `page=${previous}`)}`}
					>
						<Icon name="chevron-left" className="text-primary">
							<span className="sr-only">Previous Page</span>
						</Icon>
					</Link>
				</Button>
			)}
			{current >= 5 && (
				<>
					<Button size="sm" asChild variant={'outline'}>
						<Link
							to={`/?${stringSearchParams.replace(/page=\d+/, `page=${1}`)}`}
						>
							1
						</Link>
					</Button>
					<Button size="sm" variant={'ghost'}>
						...
					</Button>
				</>
			)}
			{previous && previous - 1 >= 1 && (
				<Button size="sm" asChild variant={'outline'}>
					<Link
						to={`/?${stringSearchParams.replace(/page=\d+/, `page=${previous - 1}`)}`}
					>
						{previous - 1}
					</Link>
				</Button>
			)}
			{previous && (
				<Button size="sm" asChild variant={'outline'}>
					<Link
						to={`/?${stringSearchParams.replace(/page=\d+/, `page=${previous}`)}`}
					>
						{previous}
					</Link>
				</Button>
			)}
			<Button
				size="sm"
				tabIndex={-1}
				variant={'default'}
				className="cursor-not-allowed"
			>
				{current}
			</Button>
			{next && (
				<Button size="sm" asChild variant={'outline'}>
					<Link
						to={`/?${stringSearchParams.replace(/page=\d+/, `page=${next}`)}`}
					>
						{next}
					</Link>
				</Button>
			)}
			{next && next + 1 <= total && (
				<Button size="sm" asChild variant={'outline'}>
					<Link
						to={`/?${stringSearchParams.replace(/page=\d+/, `page=${next + 1}`)}`}
					>
						{next + 1}
					</Link>
				</Button>
			)}
			{next && next + 3 <= total && (
				<>
					<Button size="sm" variant={'ghost'}>
						...
					</Button>
					<Button size="sm" asChild variant={'outline'}>
						<Link
							to={`/?${stringSearchParams.replace(/page=\d+/, `page=${total}`)}`}
						>
							{total}
						</Link>
					</Button>
				</>
			)}
			{next === null ? (
				<Button
					size="sm"
					tabIndex={-1}
					variant={'secondary'}
					className="cursor-not-allowed"
				>
					<Icon
						name="chevron-right"
						className="cursor-not-allowed text-primary"
					>
						<span className="sr-only">Next Page</span>
					</Icon>
				</Button>
			) : (
				<Button size="sm" asChild variant={'outline'}>
					<Link
						to={`/?${stringSearchParams.replace(/page=\d+/, `page=${next}`)}`}
					>
						<Icon name="chevron-right" className="text-primary">
							<span className="sr-only">Next Page</span>
						</Icon>
					</Link>
				</Button>
			)}
		</div>
	)
}

export default function LibraryRoute() {
	const { works, meta, user } = useLoaderData<typeof loader>()
	const [modalOpen, setModalOpen] = useState<boolean>(false)
	const [searchParams] = useSearchParams()
	searchParams.set('page', searchParams.get('page') || '1')
	return (
		<>
			<Dialog
				open={modalOpen}
				onOpenChange={() => {
					setModalOpen(!open)
				}}
				defaultOpen={modalOpen}
			>
				{/* <DialogTrigger>Open</DialogTrigger> */}
				<DialogContent className="flex max-h-[65vh] w-[calc(100vw-4rem)] max-w-screen-lg gap-0 overflow-hidden border-0 p-0 md:h-[36rem]">
					<div className="left flex flex-1 basis-1/3 flex-col gap-3 bg-background p-4 text-foreground">
						<h2 className="font-serif text-h4">
							Join Kogia&apos;s Oceanic Library
						</h2>
						<p>
							Apply for a free membership to access our library of photos and
							videos of the marine world, for you to use to communicate your
							work and tell your stories.
						</p>
						<Button asChild variant={'default'} className="mt-2">
							<Link to="/apply">Apply</Link>
						</Button>
						<div className="flex flex-1 items-end text-center">
							<p className="w-full">
								Already have an account?{' '}
								<Link to="/login" className="text-black underline">
									Login
								</Link>
							</p>
						</div>
					</div>
					<div className="left hidden flex-1 basis-2/3 bg-secondary md:flex">
						<div className="flex flex-1 flex-col justify-end gap-3 bg-[url(https://images.squarespace-cdn.com/content/v1/5f27401564a09e77e7fe96c2/91819432-40d5-4717-80c4-0103d8cb8fe1/Mainpage+Backgroundprint-enhance-2x.jpg?format=2000w)] bg-cover p-4 text-white">
							<div className="flex w-3/4 flex-col gap-3">
								<h2 className="font-serif text-h4">
									A members-only library, built for conservation.
								</h2>
								<p>
									Our library is reserved for non-profit conservation purposes.
									Access is free for smaller conservation groups, researchers,
									scientists, activists, and artists who do not have the
									resources to license media.
								</p>
								<p>
									We encourage larger organizations to sign up as Patrons in the
									application form. The monthly contributions from Patron
									members make it possible for Kogia to continue providing media
									for smaller organizations.
								</p>
							</div>
						</div>
					</div>
				</DialogContent>
			</Dialog>
			<div className="w-full">
				<Works works={works} isLoggedIn={Boolean(user)} />
			</div>
			{works.length > 0 && (
				<Pagination
					searchParams={searchParams}
					current={meta.currentPage}
					total={meta.pageCount}
					next={meta.nextPage}
					previous={meta.previousPage}
				/>
			)}
		</>
	)
}
